import {
  BOAT_TUNING,
  NERIS_BOAT_DOCKS,
  NERIS_NAVIGATION_ROUTE,
  nerisSurfaceElevationM,
} from "../../config/boats.ts";
import type { PlayerId } from "../../core/ids.ts";
import { wrapRadians } from "../../core/math.ts";
import type { InputIntent } from "../../input/intents.ts";
import type { GameState } from "../state.ts";

export const BOAT_FLEET_SCHEMA_VERSION = 1 as const;

export interface BoatState {
  readonly id: string;
  readonly label: string;
  readonly position: Readonly<{ x: number; y: number; z: number }>;
  readonly yawRadians: number;
  readonly speedMps: number;
  readonly occupiedByPlayerId: PlayerId | null;
}

export interface BoatFleetState {
  readonly schemaVersion: typeof BOAT_FLEET_SCHEMA_VERSION;
  readonly tick: number;
  readonly boatsById: Readonly<Record<string, BoatState>>;
  readonly mountedBoatIdByPlayerId: Readonly<Record<PlayerId, string>>;
}

export interface BoatPresentation {
  readonly id: string;
  readonly position: Readonly<{ x: number; y: number; z: number }>;
  readonly yawRadians: number;
  readonly speedMps: number;
  readonly occupied: boolean;
}

export interface BoatFleetStepResult {
  readonly state: BoatFleetState;
  readonly transition: "boarded" | "dismounted" | null;
  readonly interactionConsumed: boolean;
  readonly playerOverride: Readonly<{
    position: Readonly<{ x: number; y: number; z: number }>;
    facingYaw: number;
    surfaceId: string;
    mountedBoatId: string | null;
  }> | null;
}

interface RouteProjection {
  readonly x: number;
  readonly z: number;
  readonly distanceM: number;
}

function moveToward(current: number, target: number, delta: number): number {
  if (Math.abs(target - current) <= delta) {
    return target;
  }
  return current + Math.sign(target - current) * delta;
}

function nearestRouteProjection(x: number, z: number): RouteProjection {
  let nearest: RouteProjection = {
    x: NERIS_NAVIGATION_ROUTE[0]?.x ?? x,
    z: NERIS_NAVIGATION_ROUTE[0]?.z ?? z,
    distanceM: Number.POSITIVE_INFINITY,
  };
  for (let index = 1; index < NERIS_NAVIGATION_ROUTE.length; index += 1) {
    const from = NERIS_NAVIGATION_ROUTE[index - 1];
    const to = NERIS_NAVIGATION_ROUTE[index];
    if (from === undefined || to === undefined) {
      continue;
    }
    const deltaX = to.x - from.x;
    const deltaZ = to.z - from.z;
    const lengthSquared = deltaX * deltaX + deltaZ * deltaZ;
    const alpha = lengthSquared <= Number.EPSILON
      ? 0
      : Math.max(
          0,
          Math.min(
            1,
            ((x - from.x) * deltaX + (z - from.z) * deltaZ) /
              lengthSquared,
          ),
        );
    const projectedX = from.x + deltaX * alpha;
    const projectedZ = from.z + deltaZ * alpha;
    const distanceM = Math.hypot(x - projectedX, z - projectedZ);
    if (distanceM < nearest.distanceM) {
      nearest = { x: projectedX, z: projectedZ, distanceM };
    }
  }
  return nearest;
}

function nearestAvailableBoat(
  state: Readonly<BoatFleetState>,
  position: Readonly<{ x: number; z: number }>,
): BoatState | null {
  let nearest: { boat: BoatState; distanceM: number } | null = null;
  for (const boat of Object.values(state.boatsById)) {
    if (boat.occupiedByPlayerId !== null) {
      continue;
    }
    const distanceM = Math.hypot(
      boat.position.x - position.x,
      boat.position.z - position.z,
    );
    if (
      distanceM <= BOAT_TUNING.boardingRadiusM &&
      (nearest === null || distanceM < nearest.distanceM)
    ) {
      nearest = { boat, distanceM };
    }
  }
  return nearest?.boat ?? null;
}

function dockForBoat(boatId: string) {
  return NERIS_BOAT_DOCKS.find((dock) => dock.boatId === boatId) ?? null;
}

function playerOnBoat(boat: Readonly<BoatState>) {
  return {
    position: {
      x: boat.position.x,
      y: boat.position.y + BOAT_TUNING.playerDeckOffsetM,
      z: boat.position.z,
    },
    facingYaw: boat.yawRadians,
    surfaceId: `boat:${boat.id}`,
    mountedBoatId: boat.id,
  } as const;
}

export function createInitialBoatFleetState(): BoatFleetState {
  const boatsById: Record<string, BoatState> = {};
  for (const dock of NERIS_BOAT_DOCKS) {
    boatsById[dock.boatId] = {
      id: dock.boatId,
      label: `Electric riverboat ${dock.boatId.slice(-1)}`,
      position: {
        x: dock.berth.x,
        y: nerisSurfaceElevationM(dock.berth.x) + 0.08,
        z: dock.berth.z,
      },
      yawRadians: dock.yawRadians,
      speedMps: 0,
      occupiedByPlayerId: null,
    };
  }
  return {
    schemaVersion: BOAT_FLEET_SCHEMA_VERSION,
    tick: 0,
    boatsById,
    mountedBoatIdByPlayerId: {},
  };
}

export function stepBoatFleet(
  state: Readonly<BoatFleetState>,
  intent: Readonly<InputIntent>,
  playerPosition: Readonly<{ x: number; y: number; z: number }>,
  deltaSeconds: number,
): BoatFleetStepResult {
  const mountedBoatId = state.mountedBoatIdByPlayerId[intent.playerId];
  if (mountedBoatId === undefined) {
    const candidate = nearestAvailableBoat(state, playerPosition);
    if (!intent.interactPressed || candidate === null) {
      return {
        state: { ...state, tick: intent.tick },
        transition: null,
        interactionConsumed: false,
        playerOverride: null,
      };
    }
    const mounted = {
      ...candidate,
      occupiedByPlayerId: intent.playerId,
    };
    return {
      state: {
        ...state,
        tick: intent.tick,
        boatsById: { ...state.boatsById, [candidate.id]: mounted },
        mountedBoatIdByPlayerId: {
          ...state.mountedBoatIdByPlayerId,
          [intent.playerId]: candidate.id,
        },
      },
      transition: "boarded",
      interactionConsumed: true,
      playerOverride: playerOnBoat(mounted),
    };
  }

  const boat = state.boatsById[mountedBoatId];
  if (boat === undefined) {
    throw new Error(`Mounted boat ${mountedBoatId} is missing`);
  }
  const dock = dockForBoat(boat.id);
  const closeToDock = dock !== null &&
    Math.hypot(
        boat.position.x - dock.berth.x,
        boat.position.z - dock.berth.z,
      ) <= BOAT_TUNING.dockingRadiusM;
  if (
    intent.interactPressed &&
    closeToDock &&
    Math.abs(boat.speedMps) <= BOAT_TUNING.maximumDismountSpeedMps &&
    dock !== null
  ) {
    const unoccupied = {
      ...boat,
      speedMps: 0,
      occupiedByPlayerId: null,
    };
    const mountedByPlayer = { ...state.mountedBoatIdByPlayerId };
    delete mountedByPlayer[intent.playerId];
    return {
      state: {
        ...state,
        tick: intent.tick,
        boatsById: { ...state.boatsById, [boat.id]: unoccupied },
        mountedBoatIdByPlayerId: mountedByPlayer,
      },
      transition: "dismounted",
      interactionConsumed: true,
      playerOverride: {
        position: dock.exit,
        facingYaw: dock.yawRadians,
        surfaceId: `dock:${dock.id}`,
        mountedBoatId: null,
      },
    };
  }

  const throttle = Math.max(-1, Math.min(1, intent.moveZ));
  const targetSpeed = throttle >= 0
    ? throttle * BOAT_TUNING.maximumForwardSpeedMps
    : throttle * BOAT_TUNING.maximumReverseSpeedMps;
  const acceleration = throttle === 0
    ? BOAT_TUNING.dragMps2
    : Math.sign(targetSpeed) !== Math.sign(boat.speedMps) &&
        Math.abs(boat.speedMps) > 0.05
      ? BOAT_TUNING.brakingMps2
      : BOAT_TUNING.accelerationMps2;
  let speedMps = moveToward(
    boat.speedMps,
    targetSpeed,
    acceleration * deltaSeconds,
  );
  const steeringAuthority = 0.22 +
    Math.min(1, Math.abs(speedMps) / BOAT_TUNING.maximumForwardSpeedMps) *
      0.78;
  const yawRadians = wrapRadians(
    boat.yawRadians -
      intent.moveX *
        BOAT_TUNING.turnSpeedRadiansPerSecond *
        steeringAuthority *
        deltaSeconds *
        (speedMps < 0 ? -1 : 1),
  );
  let x = boat.position.x + Math.sin(yawRadians) * speedMps * deltaSeconds;
  let z = boat.position.z + Math.cos(yawRadians) * speedMps * deltaSeconds;
  const projection = nearestRouteProjection(x, z);
  if (projection.distanceM > BOAT_TUNING.navigationHalfWidthM) {
    const offsetX = x - projection.x;
    const offsetZ = z - projection.z;
    const scale = BOAT_TUNING.navigationHalfWidthM /
      Math.max(projection.distanceM, Number.EPSILON);
    x = projection.x + offsetX * scale;
    z = projection.z + offsetZ * scale;
    speedMps *= BOAT_TUNING.bankResponseSpeedScale;
  }
  const updated = {
    ...boat,
    position: {
      x,
      y: nerisSurfaceElevationM(x) + 0.08,
      z,
    },
    yawRadians,
    speedMps,
  };
  return {
    state: {
      ...state,
      tick: intent.tick,
      boatsById: { ...state.boatsById, [boat.id]: updated },
    },
    transition: null,
    interactionConsumed: intent.interactPressed,
    playerOverride: playerOnBoat(updated),
  };
}

export function boatPresentations(
  state: Readonly<BoatFleetState>,
): readonly BoatPresentation[] {
  return Object.values(state.boatsById).map((boat) => ({
    id: boat.id,
    position: boat.position,
    yawRadians: boat.yawRadians,
    speedMps: boat.speedMps,
    occupied: boat.occupiedByPlayerId !== null,
  }));
}

export function boatInteractionPrompt(
  state: Readonly<BoatFleetState>,
  playerId: PlayerId,
  playerPosition: Readonly<{ x: number; z: number }>,
): string | null {
  const mountedId = state.mountedBoatIdByPlayerId[playerId];
  if (mountedId !== undefined) {
    const boat = state.boatsById[mountedId];
    const dock = dockForBoat(mountedId);
    if (boat === undefined || dock === null) {
      return null;
    }
    const dockDistance = Math.hypot(
      boat.position.x - dock.berth.x,
      boat.position.z - dock.berth.z,
    );
    return dockDistance <= BOAT_TUNING.dockingRadiusM &&
        Math.abs(boat.speedMps) <= BOAT_TUNING.maximumDismountSpeedMps
      ? `Leave boat at ${dock.label}`
      : `W/S throttle · A/D steer · return to ${dock.label} to leave`;
  }
  const nearest = nearestAvailableBoat(state, playerPosition);
  return nearest === null ? null : `Board ${nearest.label}`;
}

export function mountedBoatId(
  state: Readonly<BoatFleetState>,
  playerId: PlayerId,
): string | null {
  return state.mountedBoatIdByPlayerId[playerId] ?? null;
}

export function applyBoatPlayerTick(
  state: Readonly<GameState>,
  intent: Readonly<InputIntent>,
  override: NonNullable<BoatFleetStepResult["playerOverride"]>,
  deltaSeconds: number,
): GameState {
  const player = state.playersById[intent.playerId];
  if (player === undefined) {
    throw new Error(`Player ${intent.playerId} is missing from game state`);
  }
  return {
    ...state,
    tick: intent.tick,
    simulationTimeSeconds: state.simulationTimeSeconds + deltaSeconds,
    playersById: {
      ...state.playersById,
      [intent.playerId]: {
        ...player,
        position: { ...override.position },
        velocity: { x: 0, y: 0, z: 0 },
        facingYaw: override.facingYaw,
        lookYaw: intent.lookYaw,
        isSprinting: false,
        locomotion: {
          ...player.locomotion,
          mode: "grounded",
          grounded: true,
          groundNormal: { x: 0, y: 1, z: 0 },
          groundSlopeRadians: 0,
          surfaceId: override.surfaceId,
          coyoteTicksRemaining: 0,
          jumpBufferTicksRemaining: 0,
          landingResponseTicksRemaining: 0,
          mantleProgress: 0,
          footstepDistanceMetres: 0,
          lastStepHeightMetres: 0,
        },
      },
    },
  };
}
